<script setup lang="ts">
import type { IPlaylistItem, TSportType } from "~/types";
import type { ISportAngle } from "~/videos/types/event-angles";
import { getCachedAngles, getCachedAnglesBySportType } from "~/videos/api/event-angles";
import { getAngleLabelFromSportAngles, getAvailableAngleNamesFromTags } from "~/videos/helpers/format-angles";

import type { ITag } from "@pixellot/web-sdk";

const props = defineProps<{
  tags: ITag[] | IPlaylistItem[];
  sportType: TSportType;
  onAngleSelected: (angle: ISportAngle) => void;
  onClose: () => void;
}>();

const { t } = useI18n();
const toast = useToast();
const selectedAngleName = ref<string>("");

// Get available angles based on sport type or all angles if no sport type
const { state: anglesBySportType, isLoading: isLoadingAngles } = useAsyncState(
  async () => {
    if (props.sportType) {
      return await getCachedAnglesBySportType(props.sportType);
    } else {
      const allAngles = await getCachedAngles();
      // Return all angles from all sport types
      return Object.values(allAngles).flat();
    }
  },
  [],
  {}
);

const anglesOptions = computed(() => {
  const angleNames = getAvailableAngleNamesFromTags(props.tags as IPlaylistItem[]);
  return angleNames.map((name) => ({
    label: getAngleLabelFromSportAngles(name, anglesBySportType.value || []),
    name,
  }));
});

const selectedAngle = computed(
  () => anglesBySportType.value?.find(({ name, shortName }) => name === selectedAngleName.value || shortName === selectedAngleName.value) || null
);

const isSubmitDisabled = computed(() => !selectedAngle.value || isLoadingAngles.value);

function handleClose() {
  props.onClose?.();
}

function handleSubmit() {
  if (!selectedAngle.value) {
    toast.error(t("errors.please_select_angle"));
    return;
  }

  props.onAngleSelected?.(selectedAngle.value);
  handleClose();
}

function handleCancel() {
  selectedAngleName.value = "";
  handleClose();
}
</script>

<template>
  <UModal appear dialog-class="w-[30rem] !max-w-[30rem]">
    <div class="p-4">
      <div class="mb-1 text-end">
        <UButton
          title="Close"
          variant="link-secondary"
          icon="close"
          class="size-5"
          @click="handleCancel"
        />
      </div>

      <div class="flex flex-col gap-6 md:px-6 md:pb-6">
        <div class="space-y-4 text-black dark:text-white">
          <h1 class="text-2xl font-bold">
            {{ t("labels.select_angle_title") }}
          </h1>
        </div>

        <div class="space-y-4">
          <UFormGroup :label="t('labels.angle')" name="angle" required>
            <USelectMenu
              v-model="selectedAngleName"
              :options="anglesOptions || []"
              :loading="isLoadingAngles"
              :placeholder="t('labels.angle_placeholder')"
              value-attribute="name"
              option-attribute="label"
              :disabled="isLoadingAngles || !anglesOptions?.length"
            />
          </UFormGroup>
        </div>

        <div class="flex gap-4">
          <UButton
            class="flex-1"
            size="lg"
            :label="t('labels.cancel')"
            variant="secondary"
            @click="handleCancel"
          />
          <UButton
            class="flex-1"
            size="lg"
            :label="t('labels.next')"
            :disabled="isSubmitDisabled"
            @click="handleSubmit"
          />
        </div>
      </div>
    </div>
  </UModal>
</template>
